/* Printora Gang Sheet — production artwork storage for cart/orders. */
(() => {
  'use strict';

  if (window.__printoraGangOrderStorageV1) return;
  window.__printoraGangOrderStorageV1 = true;

  const DPI = 300;
  const PIXELS_PER_METRE = Math.round(DPI / 0.0254);
  const TINY_PNG = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M/wHwAF/gL+JE2B1wAAAABJRU5ErkJggg==';
  let busy = false;

  function mmFromPixels(px) {
    return (Number(px) * 25.4) / DPI;
  }

  function prettyMm(value) {
    const rounded = Math.round(Number(value) * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }

  function setError(root, message) {
    const el = root?.querySelector?.('[data-pgb-error]');
    if (el) {
      el.textContent = message || '';
      el.hidden = !message;
    }
    if (message) console.error('[Printora Gang Sheet]', message);
  }

  function makeReference() {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const date = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
    const time = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
    return `GS-${date}-${time}-${rand}`;
  }

  function canvasToBlob(canvas) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Could not create the gang sheet production PNG.'));
      }, 'image/png');
    });
  }

  function readU32(bytes, offset) {
    return (
      ((bytes[offset] << 24) >>> 0) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]
    ) >>> 0;
  }

  function writeU32(bytes, offset, value) {
    const v = Number(value) >>> 0;
    bytes[offset] = (v >>> 24) & 255;
    bytes[offset + 1] = (v >>> 16) & 255;
    bytes[offset + 2] = (v >>> 8) & 255;
    bytes[offset + 3] = v & 255;
  }

  const CRC_TABLE = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) {
        c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
      }
      table[n] = c >>> 0;
    }
    return table;
  })();

  function crc32(bytes) {
    let crc = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i += 1) {
      crc = CRC_TABLE[(crc ^ bytes[i]) & 255] ^ (crc >>> 8);
    }
    return (crc ^ 0xFFFFFFFF) >>> 0;
  }

  function makePhysChunk(ppm) {
    const chunk = new Uint8Array(21);
    writeU32(chunk, 0, 9);
    chunk.set([112, 72, 89, 115], 4); // pHYs
    writeU32(chunk, 8, ppm);
    writeU32(chunk, 12, ppm);
    chunk[16] = 1; // unit = metre
    writeU32(chunk, 17, crc32(chunk.subarray(4, 17)));
    return chunk;
  }

  async function forcePngDpi(blob, dpi) {
    const ppm = Math.round(Number(dpi) / 0.0254);
    const source = new Uint8Array(await blob.arrayBuffer());
    const signature = [137, 80, 78, 71, 13, 10, 26, 10];
    if (source.length < 8 || !signature.every((byte, i) => source[i] === byte)) {
      throw new Error('Generated artwork was not a valid PNG.');
    }

    const parts = [source.subarray(0, 8)];
    let pos = 8;
    let inserted = false;

    while (pos + 12 <= source.length) {
      const length = readU32(source, pos);
      const end = pos + 12 + length;
      if (end > source.length) throw new Error('Generated PNG was incomplete.');

      const type = String.fromCharCode(
        source[pos + 4], source[pos + 5], source[pos + 6], source[pos + 7]
      );

      if (type !== 'pHYs') parts.push(source.subarray(pos, end));
      if (type === 'IHDR' && !inserted) {
        parts.push(makePhysChunk(ppm));
        inserted = true;
      }

      pos = end;
      if (type === 'IEND') break;
    }

    if (!inserted) throw new Error('Could not stamp the gang sheet with 300 DPI metadata.');
    return new Blob(parts, { type: 'image/png' });
  }

  function captureProductionCanvas(root) {
    const downloadButton = root.querySelector('[data-pgb-download]');
    if (!downloadButton) throw new Error('The production export control could not be found.');

    let capturedCanvas = null;
    const originalToDataURL = HTMLCanvasElement.prototype.toDataURL;
    const originalAnchorClick = HTMLAnchorElement.prototype.click;

    HTMLCanvasElement.prototype.toDataURL = function patchedToDataURL(type, quality) {
      const requestedType = String(type || 'image/png').toLowerCase();
      if (!capturedCanvas && requestedType.includes('png') && this.width >= 2500 && this.height > 0) {
        capturedCanvas = this;
        return TINY_PNG;
      }
      return originalToDataURL.call(this, type, quality);
    };

    HTMLAnchorElement.prototype.click = function patchedAnchorClick() {
      const filename = String(this.download || '');
      if (filename.startsWith('printora-gangsheet-')) return;
      return originalAnchorClick.call(this);
    };

    try {
      downloadButton.click();
    } finally {
      HTMLCanvasElement.prototype.toDataURL = originalToDataURL;
      HTMLAnchorElement.prototype.click = originalAnchorClick;
    }

    if (!capturedCanvas) {
      throw new Error('Could not build the print-ready gang sheet. Please try again.');
    }
    return capturedCanvas;
  }

  async function removeAddedLine(key) {
    if (!key) return;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    } catch (error) {
      console.error('[Printora Gang Sheet] Could not roll back cart line', error);
    }
  }

  async function addStoredGangSheet(button, root) {
    if (busy) return;
    busy = true;

    const form = root.querySelector('[data-pgb-form]');
    if (!form) {
      busy = false;
      setError(root, 'The gang sheet basket form could not be found.');
      return;
    }

    const originalText = button.textContent;
    setError(root, '');
    button.disabled = true;
    button.textContent = 'Preparing production file…';

    let createdLineKey = '';

    try {
      try {
        if (document.fonts?.ready) await document.fonts.ready;
      } catch (e) {}

      const canvas = captureProductionCanvas(root);
      const widthMm = mmFromPixels(canvas.width);
      const heightMm = mmFromPixels(canvas.height);
      const widthLabel = prettyMm(widthMm);
      const heightLabel = prettyMm(heightMm);
      const reference = makeReference();

      button.textContent = 'Saving gang sheet…';

      const rawPng = await canvasToBlob(canvas);
      const productionPng = await forcePngDpi(rawPng, DPI);
      const filename = `printora-${reference}-${widthLabel}x${heightLabel}mm-${DPI}dpi.png`
        .replace(/\s+/g, '-');
      const artworkFile = new File([productionPng], filename, {
        type: 'image/png',
        lastModified: Date.now()
      });

      const body = new FormData(form);
      body.set('properties[Sheet Size]', `${widthLabel} mm × ${heightLabel} mm`);
      body.set('properties[Gang Sheet Reference]', reference);
      body.set('properties[Gang Sheet Artwork]', artworkFile, artworkFile.name);
      body.set('properties[Artwork Status]', 'Production gang sheet stored with order');
      body.set('properties[Production DPI]', `${DPI} DPI`);
      body.set('properties[Production Pixels]', `${canvas.width} × ${canvas.height} px`);
      body.set('properties[_Production Width mm]', widthLabel);
      body.set('properties[_Production Height mm]', heightLabel);
      body.set('properties[_layout_hash]', reference);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        body
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not add the gang sheet to your basket.');
      }

      createdLineKey = data.key || '';
      const storedArtwork = data.properties?.['Gang Sheet Artwork'];
      if (!storedArtwork || !String(storedArtwork).trim()) {
        await removeAddedLine(createdLineKey);
        createdLineKey = '';
        throw new Error('The gang sheet was not stored by Shopify, so it was not added to your basket. Please try again.');
      }

      button.textContent = 'Added ✓';
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'gang-sheet-builder' } }));
      document.dispatchEvent(new CustomEvent('printora:gangsheet-stored', {
        detail: {
          reference,
          artworkUrl: storedArtwork,
          widthMm: Number(widthLabel),
          heightMm: Number(heightLabel),
          pixels: { width: canvas.width, height: canvas.height },
          dpi: DPI
        }
      }));

      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalText;
      }, 1600);
    } catch (error) {
      if (createdLineKey) await removeAddedLine(createdLineKey);
      setError(root, error?.message || 'There was a problem storing your gang sheet. Please try again.');
      button.disabled = false;
      button.textContent = originalText;
    } finally {
      busy = false;
    }
  }

  document.addEventListener('click', event => {
    const button = event.target?.closest?.('[data-pgb-add-cart]');
    if (!button) return;
    const root = button.closest('[data-pgb-root]');
    if (!root) return;
    if (button.disabled) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addStoredGangSheet(button, root);
  }, true);
})();
